import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Users, 
  UserCog, 
  DollarSign, 
  Clock, 
  AlertCircle,
  Plus,
  ArrowRight,
  Calendar,
  CheckSquare,
  ExternalLink,
  Phone,
  MessageSquare,
  PhoneMissed,
  Voicemail,
  UserPlus,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import QuickAssignModal from '../components/QuickAssignModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayWorkOrders, setTodayWorkOrders] = useState([]);
  const [tomorrowWorkOrders, setTomorrowWorkOrders] = useState([]);
  const [unassignedWorkOrders, setUnassignedWorkOrders] = useState([]);
  const [commsActivity, setCommsActivity] = useState(null);
  // Quick Assign Modal state
  const [quickAssignOpen, setQuickAssignOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const handleQuickAssign = (wo) => {
    setSelectedWorkOrder(wo);
    setQuickAssignOpen(true);
  };

  const refreshData = () => {
    fetchData();
  };

  const fetchData = async () => {
      try {
        const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
        
        // Fetch dashboard stats
        const statsRes = await fetch(`${API_URL}/api/reports/dashboard`, { 
          headers,
          credentials: 'include'
        });
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data);
        }

        // Fetch recent tasks
        const tasksRes = await fetch(`${API_URL}/api/tasks?status=pending&limit=5`, {
          headers,
          credentials: 'include'
        });
        if (tasksRes.ok) {
          const data = await tasksRes.json();
          setRecentTasks(data.slice(0, 5));
        }

        // Fetch today's work orders
        const todayRes = await fetch(`${API_URL}/api/reports/daily-dispatch?view=today`, {
          headers,
          credentials: 'include'
        });
        if (todayRes.ok) {
          const data = await todayRes.json();
          setTodayWorkOrders(data.data || []);
        }

        // Fetch tomorrow's work orders
        const tomorrowRes = await fetch(`${API_URL}/api/reports/daily-dispatch?view=tomorrow`, {
          headers,
          credentials: 'include'
        });
        if (tomorrowRes.ok) {
          const data = await tomorrowRes.json();
          setTomorrowWorkOrders(data.data || []);
        }

        // Fetch recent communications activity
        const commsRes = await fetch(`${API_URL}/api/communications/recent-activity?limit=5`, {
          headers,
          credentials: 'include'
        });
        if (commsRes.ok) {
          const data = await commsRes.json();
          setCommsActivity(data);
        }

        // Fetch unassigned work orders (confirmed but no vendor)
        const unassignedRes = await fetch(`${API_URL}/api/work-orders?status=confirmed`, {
          headers,
          credentials: 'include'
        });
        if (unassignedRes.ok) {
          const data = await unassignedRes.json();
          // Filter to only unassigned ones
          const unassigned = data.filter(wo => !wo.assigned_vendor_id);
          setUnassignedWorkOrders(unassigned);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchData();
  }, [getAuthHeaders]);

  const formatTime = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return 'TBD';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 11 && clean.startsWith('1')) {
      return `(${clean.slice(1, 4)}) ${clean.slice(4, 7)}-${clean.slice(7)}`;
    }
    if (clean.length === 10) {
      return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
    }
    return phone;
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const seedDemoData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/seed-demo-data`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Demo data seeded successfully!');
        window.location.reload();
      }
    } catch (error) {
      toast.error('Failed to seed demo data');
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
            alt="Bylyngo"
            className="h-16 w-auto"
          />
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-1">
              {getGreeting()}, {user?.name || 'there'}!
            </h1>
            <p className="text-blue-100 text-sm">
              Welcome to Bylyngo Language Services Platform. Here&apos;s your overview for today.
            </p>
          </div>
          <div className="hidden md:block text-right">
            <p className="text-blue-100 text-sm">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/work-orders/new">
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="new-work-order-btn">
            <Plus className="h-4 w-4" />
            New Work Order
          </Button>
        </Link>
        <Link to="/clients/new">
          <Button variant="outline" className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="new-client-btn">
            <Users className="h-4 w-4" />
            Add Client
          </Button>
        </Link>
        <Link to="/vendors/new">
          <Button variant="outline" className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="new-vendor-btn">
            <UserCog className="h-4 w-4" />
            Add Vendor
          </Button>
        </Link>
        {(!stats || stats.entities?.clients === 0) && (
          <Button variant="secondary" onClick={seedDemoData} className="gap-2 bg-orange-500 hover:bg-orange-600 text-white" data-testid="seed-demo-btn">
            Load Demo Data
          </Button>
        )}
        
        {/* Right-aligned Call, SMS & Communications buttons */}
        <div className="flex-1"></div>
        <Link to="/communications">
          <Button className="gap-2 bg-green-600 hover:bg-green-700 text-white font-bold shadow-md" data-testid="call-btn">
            <Phone className="h-5 w-5" />
            Call
          </Button>
        </Link>
        <Link to="/communications">
          <Button className="gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold shadow-md" data-testid="sms-btn">
            <MessageSquare className="h-5 w-5" />
            SMS
          </Button>
        </Link>
        <Link to="/communications">
          <Button size="sm" variant="outline" className="gap-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="missed-calls-btn">
            <PhoneMissed className="h-4 w-4" />
            Missed
          </Button>
        </Link>
        <Link to="/communications">
          <Button size="sm" variant="outline" className="gap-1 border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="missed-sms-btn">
            <MessageSquare className="h-4 w-4" />
            Inbox
          </Button>
        </Link>
        <Link to="/communications">
          <Button size="sm" variant="outline" className="gap-1 border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="voicemails-btn">
            <Voicemail className="h-4 w-4" />
            VM
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="hover:shadow-md transition-shadow border-t-4 border-t-blue-500 cursor-pointer hover:bg-blue-50/50"
          onClick={() => navigate('/work-orders')}
          data-testid="work-orders-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Work Orders</CardTitle>
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats?.work_orders?.total || 0}</div>
            <div className="flex gap-2 mt-2">
              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                {stats?.work_orders?.new_requests || 0} new
              </Badge>
              <Badge className="bg-orange-100 text-orange-700 text-xs">
                {stats?.work_orders?.in_progress || 0} active
              </Badge>
            </div>
            <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
              <span>View all</span>
              <ExternalLink className="h-3 w-3" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow border-t-4 border-t-blue-400 cursor-pointer hover:bg-blue-50/50"
          onClick={() => navigate('/calendar')}
          data-testid="todays-schedule-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Today&apos;s Schedule</CardTitle>
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Calendar className="h-4 w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats?.today?.interpreting_assignments || 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Interpreting assignments</p>
            <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
              <span>View calendar</span>
              <ExternalLink className="h-3 w-3" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow border-t-4 border-t-green-500 cursor-pointer hover:bg-green-50/50"
          onClick={() => navigate('/invoices?status=sent')}
          data-testid="outstanding-ar-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Outstanding AR</CardTitle>
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              ${(stats?.financial?.outstanding_ar || 0).toLocaleString()}
            </div>
            <p className="text-sm text-muted-foreground mt-2">Unpaid invoices</p>
            <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
              <span>View invoices</span>
              <ExternalLink className="h-3 w-3" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow border-t-4 border-t-orange-500 cursor-pointer hover:bg-orange-50/50"
          onClick={() => navigate('/tasks?status=pending')}
          data-testid="pending-tasks-card"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Tasks</CardTitle>
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
              <CheckSquare className="h-4 w-4 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{stats?.tasks?.pending || 0}</div>
            {stats?.tasks?.overdue > 0 && (
              <Badge variant="destructive" className="mt-2">
                {stats.tasks.overdue} overdue
              </Badge>
            )}
            <div className="flex items-center gap-1 mt-2 text-xs text-orange-600">
              <span>View tasks</span>
              <ExternalLink className="h-3 w-3" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's & Tomorrow's Work Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Work Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between rounded-t-lg" style={{background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)'}}>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Today&apos;s Work Orders
            </CardTitle>
            <Link to="/calendar">
              <Button variant="ghost" size="sm" className="gap-1 text-white hover:text-yellow-100 hover:bg-white/10">
                View all <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {todayWorkOrders.length > 0 ? (
              <div className="divide-y">
                {todayWorkOrders.slice(0, 5).map((wo, i) => (
                  <div 
                    key={wo.work_order_id || i}
                    className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/work-orders/${wo.work_order_id}`)}
                    data-testid={`today-wo-${i}`}
                  >
                    <div className="w-16 text-center">
                      <span className="text-sm font-bold text-blue-600">{formatTime(wo.start_datetime)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-blue-700 hover:underline">
                        {wo.work_order_number || wo.work_order_id}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">{wo.client_name}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={wo.interpreter === 'UNASSIGNED' ? 'destructive' : 'secondary'} className="text-xs">
                        {wo.interpreter === 'UNASSIGNED' ? 'Unassigned' : wo.interpreter?.split(' ')[0]}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">{wo.languages?.join(', ')}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  </div>
                ))}
                {todayWorkOrders.length > 5 && (
                  <div className="p-2 text-center bg-slate-50">
                    <Link to="/calendar" className="text-sm text-blue-600 hover:underline">
                      +{todayWorkOrders.length - 5} more assignments
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No work orders scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tomorrow's Work Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between rounded-t-lg" style={{background: 'linear-gradient(135deg, #0968e5 0%, #091970 100%)'}}>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Tomorrow&apos;s Work Orders
            </CardTitle>
            <Link to="/calendar">
              <Button variant="ghost" size="sm" className="gap-1 text-white hover:text-blue-100 hover:bg-white/10">
                View all <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {tomorrowWorkOrders.length > 0 ? (
              <div className="divide-y">
                {tomorrowWorkOrders.slice(0, 5).map((wo, i) => (
                  <div 
                    key={wo.work_order_id || i}
                    className="flex items-center gap-3 p-3 hover:bg-orange-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/work-orders/${wo.work_order_id}`)}
                    data-testid={`tomorrow-wo-${i}`}
                  >
                    <div className="w-16 text-center">
                      <span className="text-sm font-bold text-orange-600">{formatTime(wo.start_datetime)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-orange-700 hover:underline">
                        {wo.work_order_number || wo.work_order_id}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">{wo.client_name}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={wo.interpreter === 'UNASSIGNED' ? 'destructive' : 'outline'} className="text-xs">
                        {wo.interpreter === 'UNASSIGNED' ? 'Unassigned' : wo.interpreter?.split(' ')[0]}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">{wo.languages?.join(', ')}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  </div>
                ))}
                {tomorrowWorkOrders.length > 5 && (
                  <div className="p-2 text-center bg-slate-50">
                    <Link to="/calendar" className="text-sm text-orange-600 hover:underline">
                      +{tomorrowWorkOrders.length - 5} more assignments
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No work orders scheduled for tomorrow</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Unassigned Work Orders Section */}
      {unassignedWorkOrders.length > 0 && (
        <Card className="border-orange-300 border-2">
          <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-orange-500 to-red-500 rounded-t-lg">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Unassigned Work Orders ({unassignedWorkOrders.length})
            </CardTitle>
            <Link to="/work-orders?status=confirmed">
              <Button variant="ghost" size="sm" className="gap-1 text-white hover:text-orange-100 hover:bg-white/10">
                View all <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {unassignedWorkOrders.slice(0, 6).map((wo, i) => (
                <div 
                  key={wo.work_order_id || i}
                  className="flex items-center gap-3 p-3 hover:bg-orange-50 transition-colors"
                  data-testid={`unassigned-wo-${i}`}
                >
                  <div className="w-20 text-center">
                    <span className="text-sm font-bold text-orange-600">{formatTime(wo.interpreting_details?.start_datetime || wo.start_datetime)}</span>
                    <p className="text-xs text-muted-foreground">
                      {wo.interpreting_details?.start_datetime ? new Date(wo.interpreting_details.start_datetime).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}) : ''}
                    </p>
                  </div>
                  <div 
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/work-orders/${wo.work_order_id}`)}
                  >
                    <p className="font-medium text-sm truncate text-blue-600 hover:underline">{wo.work_order_id}</p>
                    <p className="text-xs text-muted-foreground truncate">{wo.client_name}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge variant="outline" className="text-xs">{wo.target_language || wo.languages?.[1] || 'N/A'}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{wo.interpreting_details?.modality || 'N/A'}</Badge>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs capitalize">{wo.status?.replace(/_/g, ' ')}</Badge>
                  <Button 
                    size="sm" 
                    className="gap-1 bg-blue-600 hover:bg-blue-700" 
                    title="Quick Assign Vendor"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAssign(wo);
                    }}
                    data-testid={`quick-assign-btn-${i}`}
                  >
                    <UserPlus className="h-4 w-4" />
                    Assign
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Assign Modal */}
      <QuickAssignModal
        open={quickAssignOpen}
        onOpenChange={setQuickAssignOpen}
        workOrder={selectedWorkOrder}
        onAssigned={refreshData}
      />

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Attention Required */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between bg-blue-50 rounded-t-lg">
            <CardTitle className="text-lg font-semibold text-blue-800">Attention Required</CardTitle>
            <Link to="/work-orders?status=new_request">
              <Button variant="ghost" size="sm" className="gap-1 text-blue-600 hover:text-blue-800">
                View all <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="pt-4">
            {stats?.work_orders?.unassigned > 0 || stats?.work_orders?.new_requests > 0 ? (
              <div className="space-y-3">
                {stats?.work_orders?.new_requests > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-800">{stats.work_orders.new_requests} new request(s)</p>
                      <p className="text-sm text-blue-600">Awaiting confirmation</p>
                    </div>
                    <Link to="/work-orders?status=new_request">
                      <Button size="sm" variant="outline" className="border-blue-300 text-blue-700">Review</Button>
                    </Link>
                  </div>
                )}
                {stats?.work_orders?.unassigned > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    <div className="flex-1">
                      <p className="font-medium text-orange-800">{stats.work_orders.unassigned} unassigned work order(s)</p>
                      <p className="text-sm text-orange-600">Need vendor assignment</p>
                    </div>
                    <Link to="/work-orders?status=confirmed">
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700">Assign Now</Button>
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckSquare className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-70" />
                <p className="text-green-600 font-medium">All work orders are assigned!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Entity Counts */}
        <Card>
          <CardHeader className="bg-blue-50 rounded-t-lg">
            <CardTitle className="text-lg font-semibold text-blue-800">Quick Stats</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <Link to="/clients" className="flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <span className="font-medium">Clients</span>
              </div>
              <span className="text-2xl font-bold text-blue-600">{stats?.entities?.clients || 0}</span>
            </Link>
            <Link to="/vendors" className="flex items-center justify-between p-3 rounded-lg hover:bg-orange-50 transition-colors border border-transparent hover:border-orange-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <UserCog className="h-5 w-5 text-orange-600" />
                </div>
                <span className="font-medium">Vendors</span>
              </div>
              <span className="text-2xl font-bold text-orange-600">{stats?.entities?.vendors || 0}</span>
            </Link>
            <Link to="/payables?status=pending" className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-100 hover:bg-green-100 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <span className="font-medium">Pending AP</span>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-green-600">${(stats?.financial?.outstanding_ap || 0).toLocaleString()}</span>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <span>View</span>
                  <ExternalLink className="h-3 w-3" />
                </div>
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Tasks Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between bg-blue-50 rounded-t-lg">
          <CardTitle className="text-lg font-semibold text-blue-800">Pending Tasks</CardTitle>
          <Link to="/tasks">
            <Button variant="ghost" size="sm" className="gap-1 text-blue-600 hover:text-blue-800">
              View all <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="pt-4">
          {recentTasks.length > 0 ? (
            <div className="space-y-3">
              {recentTasks.map((task) => (
                <div 
                  key={task.task_id} 
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors cursor-pointer"
                  onClick={() => navigate(`/tasks/${task.task_id}`)}
                  data-testid={`task-item-${task.task_id}`}
                >
                  <div className={`w-2 h-2 rounded-full ${
                    task.priority === 'urgent' ? 'bg-red-500' :
                    task.priority === 'high' ? 'bg-orange-500' :
                    task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-blue-700 hover:underline">{task.title}</p>
                    <p className="text-sm text-muted-foreground truncate">{task.description}</p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {new Date(task.due_date).toLocaleDateString()}
                  </div>
                  <ExternalLink className="h-4 w-4 text-slate-400" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckSquare className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-70" />
              <p className="text-green-600 font-medium">No pending tasks</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
